import { AlertTriangle, ArrowRight, XClose } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { PipelineStageValueLabel } from '@/lib/crm/components/properties/labels/PipelineStageValueLabel';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import { PropertyInput } from '@/lib/crm/components/properties/PropertyInput';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { cx } from '@/utils/cx';

import { StageTransitionPreview } from './StageTransitionPreview';

/**
 * Single row in the property diff table
 */
export interface PropertyDiffRow {
  /** Property type name (e.g., "ahoy_email") */
  propertyName: string;
  /** Human-readable label (e.g., "Email") */
  propertyLabel: string;
  /** Field type for determining input component (e.g., "email", "text", "number") */
  fieldType: string;
  /** Current value in the database */
  currentValue: unknown;
  /** Proposed new value */
  proposedValue: unknown;
  /** Original value when proposal was created (for conflict detection) */
  originalValue: unknown;
  /** True if currentValue !== originalValue (conflict detected) */
  hasConflict: boolean;
  /** Property type schema for input component */
  propertySchema?: PropertyTypeSchema;
}

interface PropertyDiffTableProps {
  rows: PropertyDiffRow[];
  editable: boolean;
  onValueChange: (propertyName: string, newValue: unknown) => void;
  entityType: string;
  /**
   * The entity's raw `properties` map, forwarded to PropertyValueLabel /
   * PropertyInput so a `formula` row can resolve its target entity type from
   * a sibling property. Undefined for non-formula rows (harmless — ignored).
   */
  entityProperties?: Record<string, unknown>;
  /** Organization ID, forwarded to PropertyInput for formula uuid-literal badges. */
  orgId?: number;
}

/**
 * Compare two values for equality (deep comparison for objects)
 */
function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (a === null || b === null) {
    return a === b;
  }
  if (typeof a !== typeof b) {
    return false;
  }
  if (typeof a === 'object' && typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

/**
 * Get the stage-compatible entity type from CRM entity type
 */
function getStageEntityType(entityType: string): 'ahoy_deal' | 'ahoy_contact' {
  if (entityType === 'ahoy_contact') {
    return 'ahoy_contact';
  }
  return 'ahoy_deal';
}

interface PropertyDiffTableRowProps {
  row: PropertyDiffRow;
  editable: boolean;
  onValueChange: (propertyName: string, newValue: unknown) => void;
  entityType: string;
  entityProperties?: Record<string, unknown>;
  orgId?: number;
}

function PropertyDiffTableRow({
  row,
  editable,
  onValueChange,
  entityType,
  entityProperties,
  orgId,
}: PropertyDiffTableRowProps) {
  const handleChange = useCallback(
    (newValue: unknown) => onValueChange(row.propertyName, newValue),
    [onValueChange, row.propertyName]
  );
  const handleClear = useCallback(
    () => onValueChange(row.propertyName, null),
    [onValueChange, row.propertyName]
  );

  const isChanged = !valuesEqual(row.currentValue, row.proposedValue);
  const isPipelineStage = row.fieldType === 'pipeline_stage';

  // Stable schema reference: building a fresh fallback object on every render
  // gives PropertyValueLabel/PropertyInput a new `propertyType` prop each pass,
  // breaking their internal memoization and forcing re-renders of inputs and
  // labels even when nothing about the row changed. useMemo scopes the
  // fallback to the inputs that actually feed it.
  const propertySchema = useMemo<PropertyTypeSchema>(
    () =>
      row.propertySchema || {
        property_type_name: row.propertyName,
        entity_type_name: entityType,
        label: row.propertyLabel,
        field_type_name: row.fieldType,
        description: '',
        is_required: false,
        is_unique_value: false,
        value_write_access: 'all',
        value_read_access: 'all',
        value_update_access: 'all',
      },
    [
      row.propertySchema,
      row.propertyName,
      row.propertyLabel,
      row.fieldType,
      entityType,
    ]
  );

  if (isPipelineStage && !editable) {
    return (
      <div className='border border-border-secondary rounded-xl p-5 bg-bg-primary'>
        {/* Property Label and Conflict Warning */}
        <div className='flex items-center gap-2 mb-4'>
          <span className='font-semibold text-sm text-fg-primary'>
            {row.propertyLabel}
          </span>
          {row.hasConflict && (
            <div
              className='flex items-center gap-1 px-2 py-0.5 rounded-full bg-utility-warning-50 text-utility-warning-700 text-xs font-medium ring-1 ring-inset ring-utility-warning-200'
              title='This value changed since the action was created'
            >
              <AlertTriangle className='w-3 h-3' />
              <span>Changed</span>
            </div>
          )}
        </div>

        {/* Stage Transition Preview - now uses entity_id values */}
        <StageTransitionPreview
          currentValue={row.currentValue as string | null | undefined}
          proposedValue={row.proposedValue as string | null | undefined}
          entityType={getStageEntityType(entityType)}
          showPipeline={true}
        />

        {/* Conflict Original Value */}
        {row.hasConflict && (
          <div className='mt-4 pt-3 border-t border-border-secondary text-xs text-utility-warning-600'>
            Original value when proposed:{' '}
            <PipelineStageValueLabel
              value={row.originalValue as string | null | undefined}
              fontSize='sm'
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className='rounded-xl border border-border-secondary bg-bg-primary overflow-hidden'>
      {/* Property Label Header */}
      <div className='flex items-center justify-between px-4 py-3 border-b border-border-secondary bg-bg-secondary'>
        <span className='font-semibold text-sm text-fg-primary'>
          {row.propertyLabel}
        </span>
        {row.hasConflict && (
          <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-utility-warning-100 text-utility-warning-700 ring-1 ring-inset ring-utility-warning-200'>
            <AlertTriangle className='w-3 h-3' />
            Changed
          </span>
        )}
      </div>

      {/* Two Column Layout: Current and Proposed */}
      <div className='p-4'>
        <div className='flex items-center gap-3'>
          {/* Current Value */}
          <div className='flex-1'>
            <div className='text-xs font-medium text-fg-quaternary uppercase tracking-wide mb-1.5'>
              Current
            </div>
            <div
              className={cx(
                'px-3 py-2.5 rounded-lg text-sm',
                row.hasConflict
                  ? 'bg-utility-warning-50 text-utility-warning-700 ring-1 ring-inset ring-utility-warning-200'
                  : 'bg-bg-secondary text-fg-secondary ring-1 ring-inset ring-border-secondary'
              )}
            >
              {isPipelineStage ? (
                <PipelineStageValueLabel
                  value={row.currentValue as string | null | undefined}
                  fontSize='sm'
                />
              ) : (
                <PropertyValueLabel
                  value={row.currentValue}
                  propertyType={propertySchema}
                  fontSize='sm'
                  entityProperties={entityProperties}
                  orgId={orgId}
                />
              )}
            </div>
          </div>

          {/* Arrow Indicator */}
          {isChanged && !editable && (
            <div className='flex items-center justify-center w-8 h-8 rounded-full bg-utility-brand-50 text-utility-brand-600 mt-5'>
              <ArrowRight className='w-4 h-4' />
            </div>
          )}

          {/* Proposed Value */}
          <div className='flex-1'>
            <div className='text-xs font-medium text-fg-quaternary uppercase tracking-wide mb-1.5'>
              Proposed
            </div>
            {editable ? (
              <div className='flex items-start gap-2'>
                <div className='flex-1 [&_label]:hidden [&_input]:!mt-0 [&_.input-wrapper]:!mt-0'>
                  <PropertyInput
                    propertyType={propertySchema}
                    value={
                      row.proposedValue as
                        | string
                        | number
                        | object
                        | null
                        | undefined
                    }
                    onChange={handleChange}
                    disabled={false}
                    entityProperties={entityProperties}
                    orgId={orgId}
                  />
                </div>
                {/* Clear button for non-required fields */}
                {!propertySchema.is_required && row.proposedValue !== null && (
                  <button
                    type='button'
                    onClick={handleClear}
                    className='mt-2 p-1.5 rounded-lg text-fg-quaternary hover:text-fg-secondary hover:bg-bg-secondary transition-colors'
                    title='Clear value'
                    aria-label='Clear value'
                  >
                    <XClose className='w-4 h-4' />
                  </button>
                )}
              </div>
            ) : (
              <div
                className={cx(
                  'px-3 py-2.5 rounded-lg text-sm',
                  isChanged
                    ? 'bg-utility-success-50 text-utility-success-700 font-medium ring-1 ring-inset ring-utility-success-200'
                    : 'bg-bg-secondary text-fg-primary ring-1 ring-inset ring-border-secondary'
                )}
              >
                {isPipelineStage ? (
                  <PipelineStageValueLabel
                    value={row.proposedValue as string | null | undefined}
                    fontSize='sm'
                  />
                ) : (
                  <PropertyValueLabel
                    value={row.proposedValue}
                    propertyType={propertySchema}
                    fontSize='sm'
                    entityProperties={entityProperties}
                    orgId={orgId}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Conflict Original Value */}
        {row.hasConflict && (
          <div className='mt-3 pt-3 border-t border-border-secondary text-xs text-utility-warning-600'>
            Original value when proposed:{' '}
            {isPipelineStage ? (
              <PipelineStageValueLabel
                value={row.originalValue as string | null | undefined}
                fontSize='sm'
              />
            ) : (
              <PropertyValueLabel
                value={row.originalValue}
                propertyType={propertySchema}
                fontSize='sm'
                entityProperties={entityProperties}
                orgId={orgId}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * PropertyDiffTable - Side-by-side diff table showing current vs proposed property values.
 *
 * Displays a table with three columns: Property, Current Value, Proposed Value.
 * Shows conflict warnings when current value differs from original value (another user
 * or process changed the value since the proposal was created).
 *
 * @example
 * <PropertyDiffTable
 *   rows={diffRows}
 *   editable={proposal.resolution === 'pending'}
 *   onValueChange={(prop, val) => handleChange(prop, val)}
 *   entityType="ahoy_contact"
 * />
 */
export function PropertyDiffTable({
  rows,
  editable,
  onValueChange,
  entityType,
  entityProperties,
  orgId,
}: PropertyDiffTableProps) {
  if (rows.length === 0) {
    return (
      <div className='text-sm text-fg-tertiary italic py-4'>
        No property changes in this action.
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {rows.map(row => (
        <PropertyDiffTableRow
          key={row.propertyName}
          row={row}
          editable={editable}
          onValueChange={onValueChange}
          entityType={entityType}
          entityProperties={entityProperties}
          orgId={orgId}
        />
      ))}
    </div>
  );
}
